"use client";

import { useEffect } from "react";
import Link from "next/link";
import { IconScale, IconRefresh, IconArrowLeft, IconAlertCircle } from "@tabler/icons-react";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Log error to client console and report to backend
    console.error("[LawChat App Error]", error);
    try {
      fetch("/api/antigravity-chat/report-error", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          errorType: "Next.js App Runtime Error",
          errorMessage: error?.message || "Eroare runtime React / Next.js",
          stackTrace: error?.stack || error?.digest || null,
          route: typeof window !== "undefined" ? window.location.pathname : undefined,
        }),
      }).catch(() => {});
    } catch {}
  }, [error]);

  return (
    <div className="min-h-screen bg-[#F8F5F1] dark:bg-[#141210] flex flex-col items-center justify-center p-4 sm:p-6 text-[#2C1810] dark:text-[#EDE8E3]">
      <div className="w-full max-w-lg bg-white dark:bg-[#1F1B18] border border-[#E8DFD8] dark:border-[#332B25] rounded-2xl shadow-xl p-8 sm:p-10 text-center relative overflow-hidden">
        {/* Top decorative gradient bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#C4622D] via-[#D97706] to-[#C4622D]" />

        {/* Logo */}
        <div className="inline-flex items-center gap-2.5 mb-6">
          <div className="w-11 h-11 rounded-xl bg-[#FBF0EA] dark:bg-[#2D221D] flex items-center justify-center text-[#C4622D] dark:text-[#DA7756]">
            <IconScale className="size-6" />
          </div>
          <span className="text-xl font-bold tracking-tight text-[#2C1810] dark:text-[#EDE8E3]">
            LawChat
          </span>
          <span className="text-[11px] font-semibold bg-[#C4622D] text-white px-2 py-0.5 rounded-md uppercase tracking-wider">
            Pro
          </span>
        </div>

        {/* Status Badge */}
        <div className="inline-flex items-center gap-2 bg-[#FEF3C7] dark:bg-[#2D2314] text-[#92400E] dark:text-[#FBBF24] text-xs font-semibold px-3.5 py-1.5 rounded-full mb-5">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D97706] opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#D97706]"></span>
          </span>
          <span>Actualizare sau Mentenanță Temporară</span>
        </div>

        <h1 className="text-2xl font-bold text-[#2C1810] dark:text-[#EDE8E3] mb-3">
          Serviciul întâmpină o întrerupere
        </h1>

        <p className="text-sm text-[#6B5E57] dark:text-[#A39992] mb-6 leading-relaxed">
          Platforma întâmpină o scurtă indisponibilitate tehnică. Echipa noastră a fost deja notificată automat și lucrează la restabilirea imediată a serviciilor.
        </p>

        <div className="bg-[#F8F5F1] dark:bg-[#141210] border border-[#E8DFD8] dark:border-[#332B25] rounded-xl p-4 mb-7 text-xs text-[#6B5E57] dark:text-[#A39992] flex items-start gap-3 text-left">
          <IconAlertCircle className="size-5 text-[#C4622D] dark:text-[#DA7756] shrink-0 mt-0.5" />
          <div>
            <strong className="text-[#2C1810] dark:text-[#EDE8E3]">Datele tale sunt în deplină siguranță.</strong><br />
            Documentele, conversațiile și dosarele tale nu sunt afectate. Serviciul revine online în câteva momente.
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={() => reset()}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#C4622D] hover:bg-[#A0501F] text-white text-sm font-semibold shadow-sm transition-all"
          >
            <IconRefresh className="size-4" />
            Reîncearcă
          </button>
          <Link
            href="/dashboard"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-[#E8DFD8] dark:border-[#332B25] hover:bg-[#F8F5F1] dark:hover:bg-[#141210] text-[#2C1810] dark:text-[#EDE8E3] text-sm font-semibold transition-all"
          >
            <IconArrowLeft className="size-4" />
            Înapoi la Dashboard
          </Link>
        </div>
      </div>

      <p className="mt-6 text-xs text-[#6B5E57] dark:text-[#A39992]">
        Ai nevoie de asistență urgentă? Scrie-ne la{" "}
        <a href="mailto:contact@lawchat.ro" className="text-[#C4622D] dark:text-[#DA7756] underline">
          contact@lawchat.ro
        </a>
      </p>
    </div>
  );
}
